import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Headphones, Mic, PenLine, type LucideIcon } from 'lucide-react';
import type { IeltsSkillKey } from 'modules/result/types';
import type { SkillProgress } from './ieltsSummary';

const ICONS: Record<IeltsSkillKey, LucideIcon> = { listening: Headphones, reading: BookOpen, writing: PenLine, speaking: Mic };
const BAND_SCALE_MAX = 9;

interface SkillCardsProps {
  skills: SkillProgress[];
  /** The learner's goal band; the bar fills toward it, or toward 9.0 without a goal. */
  target: number | null;
  accent: string;
}

/** One card per IELTS skill: finished attempts, best band and how far that band is toward the goal. */
export function SkillCards({ skills, target, accent }: SkillCardsProps) {
  const { t, i18n } = useTranslation();
  const goal = target ?? BAND_SCALE_MAX;
  const fmt = (band: number) => band.toLocaleString(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return (
    <section aria-labelledby="ielts-skills">
      <h2 id="ielts-skills" className="font-display text-[19px] font-extrabold tracking-[-0.03em] text-foreground">{t('portal.ielts_skills_title')}</h2>
      <ul className="mt-3.5 grid grid-cols-2 gap-3 md:grid-cols-4">
        {skills.map(({ skill, attempts, best }) => {
          const Icon = ICONS[skill];
          const pct = best === null ? 0 : Math.min(100, Math.round((best / goal) * 100));
          return (
            <li key={skill} className="relative overflow-hidden rounded-[18px] border border-border bg-card p-4">
              <Icon aria-hidden className="pointer-events-none absolute -right-3 -top-3 h-20 w-20 opacity-[0.08]" style={{ color: accent }} />
              <p className="relative text-[15px] font-extrabold text-foreground">{t(`portal.ielts_skill_${skill}`)}</p>
              <p className="relative mt-1 text-xs text-muted-foreground">
                {attempts > 0
                  ? `${t('portal.ielts_skill_attempts', { count: attempts })}${best !== null ? ` · ${t('portal.ielts_skill_best', { band: fmt(best) })}` : ''}`
                  : t('portal.ielts_skill_none')}
              </p>
              <div
                role="progressbar"
                aria-label={t(`portal.ielts_skill_${skill}`)}
                aria-valuemin={0}
                aria-valuemax={goal}
                aria-valuenow={best ?? 0}
                className="relative mt-3.5 h-1.5 overflow-hidden rounded-full bg-border"
              >
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: accent }} />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export default SkillCards;
